/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import React, {
  FocusEvent,
  KeyboardEvent,
  useState,
  useEffect,
  FunctionComponent,
} from 'react';
import Input from './Input';

interface CachedInputProps {
  type?: string;
  value?: any;
  onChange?: (value: string | boolean) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * Caches the user's input when focused for a better editing experience
 * (avoids issues like undeletable 0s)
 */
const CachedInput: FunctionComponent<CachedInputProps> = props => {
  const [cachedValue, setCachedValue] = useState(props.value);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) {
      setCachedValue(props.value);
    }
  }, [focused, props.value]);

  const onChange = (value: string | boolean) => {
    // 0 is valid, but 00 is not valid.
    // Force display value to '0' if the first 2 characters are '00'
    const cleanValue =
      typeof value === 'string' && value.search('00') === 0 ? '0' : value;
    setCachedValue(cleanValue);
    if (props.onChange) {
      props.onChange(cleanValue);
    }
  };
  const onFocus = (e: FocusEvent<HTMLInputElement>) => {
    setFocused(true);
    // If value is 0
    // Set display to show ''
    // This forces the placeholder value to display
    if (cachedValue === 0) {
      setCachedValue('');
    }
    if (props.onFocus) {
      props.onFocus(e);
    }
  };
  const onBlur = (e: FocusEvent<HTMLInputElement>) => {
    setFocused(false);
    setCachedValue(props.value);
    if (props.onBlur) {
      props.onBlur(e);
    }
  };

  return (
    <Input
      {...props}
      onChange={onChange}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={props.onKeyDown}
      value={cachedValue}
    />
  );
};

CachedInput.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onKeyDown: PropTypes.func,
  className: PropTypes.string,
  type: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
};

export default CachedInput;
